import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { Menu, Users, Building2, BarChart3, LogOut, UserCircle } from "lucide-react";
import { Sidebar, type NavItem } from "@/components/layout/Sidebar";
import { useAuthContext } from "@/context/AuthContext";
import { logout } from "@/lib/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const COLLAPSED_STORAGE_KEY = "admin_sidebar_collapsed";

const ADMIN_NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", href: "/admin/stats", icon: BarChart3 },
  { label: "Users", href: "/admin/users", icon: Users },
  { label: "Companies", href: "/admin/companies", icon: Building2 },
];

export function AdminShell({ children }: { children: ReactNode }) {
  const [, setLocation] = useLocation();
  const { user } = useAuthContext();
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true"
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  async function handleLogout() {
    try {
      await logout();
    } finally {
      setLocation("/login");
    }
  }

  const displayName = `${user.firstName} ${user.lastName}`;
  const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  const sidebarWidth = collapsed ? "md:pl-16" : "md:pl-60";

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        groups={[{ label: "ADMIN", items: ADMIN_NAV_ITEMS }]}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div className="sticky top-0 z-30 flex h-14 items-center border-b border-border bg-background px-4 md:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          className="rounded-lg p-2 text-muted-foreground hover:bg-accent"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <div className={`transition-[padding] duration-200 ${sidebarWidth}`}>
        <div className="sticky top-0 z-20 hidden h-16 items-center justify-end border-b border-border bg-background px-6 md:flex md:px-8">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-[10px] font-semibold text-accent-foreground">
                  {initials}
                </div>
                <div className="min-w-0 text-left">
                  <div className="truncate text-xs font-semibold text-foreground">{displayName}</div>
                  <div className="truncate text-[10px] text-muted-foreground">Platform Admin</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="end" sideOffset={8} className="w-56">
              <DropdownMenuLabel>
                <div className="truncate text-xs font-medium">{displayName}</div>
                <div className="truncate text-[11px] font-normal text-muted-foreground">Platform Admin</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => setLocation("/admin/profile")}>
                <UserCircle className="h-3.5 w-3.5" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                <LogOut className="h-3.5 w-3.5" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <main className="px-6 py-6 md:px-8 md:py-8">{children}</main>
      </div>
    </div>
  );
}
